import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo7Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 7 BWV 778
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV778/bach-invention-07/bach-invention-07-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 7 BWV 778.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-5342">
      <Navbar data-id="element-5343" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-5344">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-5345">
          <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop" alt="Invention No. 7" className="w-full h-64 md:h-80 object-cover" data-id="element-5346" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-5347" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-5348">
            <div className="max-w-7xl mx-auto" data-id="element-5349">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-5350">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-5351" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-5352">
                Invention No. 7 in E minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-5353">BWV 778</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-5354">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-5355">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-5356">
          <div className="flex items-center space-x-2" data-id="element-5357">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-5358">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-5359" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5360">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-5361">
                  <ZoomInIcon className="h-5 w-5" data-id="element-5362" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-5363">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-5364">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-5365" />
              <span className="hidden sm:inline" data-id="element-5366">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-5367">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-5368" />
              <span className="hidden sm:inline" data-id="element-5369">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-5370">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5371">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-5372">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 7 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-5373" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5374">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5375">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-5376">
            <div data-id="element-5377">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5378">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-5379">
                <div data-id="element-5380">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5381">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5382">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-5383">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5384">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5385">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-5386">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5387">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5388">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-5389">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5390">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5391">
                    E minor
                  </dd>
                </div>
                <div data-id="element-5392">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5393">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5394">
                    BWV 778
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-5395">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5396">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5397">
                The seventh of Bach's fifteen Two-Part Inventions, this piece in
                E minor is notable for its energetic character and perpetual
                motion. Written as pedagogical works for keyboard students, the
                Inventions teach the art of two-voice counterpoint while
                developing musical ideas through systematic elaboration. This
                invention features a lively, running sixteenth-note subject that
                creates a sense of continuous forward momentum throughout the
                piece. The minor key lends the work a serious, focused quality,
                while the constant rhythmic activity demands precise finger work
                and evenness from the performer. The piece demonstrates Bach's
                mastery of invertible counterpoint, with the two voices
                frequently exchanging roles in a seamless dialogue. The
                technical challenges include maintaining clarity in the rapid
                passages, managing the hand crossings that occur when the voices
                intersect, and bringing out the contrapuntal lines while
                maintaining the overall sense of drive and direction. This
                invention serves as both an essential study in baroque keyboard
                technique and a compelling musical statement that showcases
                Bach's genius for creating sophisticated musical architecture
                within a compact, pedagogical framework.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-5398" />
    </div>;
};